import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useRefresh } from '../context/RefreshContext';
import { useColorMode } from '../context/ColorModeContext';
import {
  AppBar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Button,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Tooltip,
  CircularProgress,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  Fab,
  Zoom
} from '@mui/material';
import {
  Add,
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  LocationCity as SitesIcon,
  Devices as DevicesIcon,
  Receipt as BillingIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  WaterDrop as WaterDropIcon,
  AccountCircle as AccountIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon,
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon,
  MonitorHeart as MonitorIcon
} from '@mui/icons-material';

const drawerWidth = 240;

function Layout() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const { user, permissions, logout } = useAuth();
  const { refreshData, isRefreshing, lastRefreshTime } = useRefresh();
  const { mode, toggleColorMode } = useColorMode();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleProfileMenuClose();
    logout();
  };

  // Filter menu items based on user role
  const getMenuItems = () => {
    const userAccounts = Array.isArray(permissions?.accounts) ? permissions.accounts : [];
    const hasLinkedAccounts = userAccounts.length > 0;
    const canSeeBilling = hasLinkedAccounts && (user?.role === 'admin' || user?.role === 'operator' || user?.role === 'super_user');

    const allMenuItems = [
      { text: 'Sites', icon: <DashboardIcon />, path: '/sites' },
      { text: 'Monitor', icon: <MonitorIcon />, path: '/monitor' },
      { text: 'Devices', icon: <DevicesIcon />, path: '/devices' },
      { text: 'Billing', icon: <BillingIcon />, path: '/accounts' },
      { text: 'Users', icon: <PersonIcon />, path: '/users' },
      { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
    ];
    
    // Filter menu items based on user role
    if (user && user.role) {
      return allMenuItems.filter(item => {
        // Billing tab is shown only for users linked to accounts
        if (item.text === 'Billing') return canSeeBilling;

        // Admin and operator can access everything
        if (user.role === 'admin' || user.role === 'operator') return true;

        // Users page is restricted to admin users only
        if (item.text === 'Users') return user.role === 'admin';
        
        // Super users can access everything except Settings
        if (user.role === 'super_user' && item.text !== 'Settings') return true;
        
        // End users can only access Sites and Devices
        if (user.role === 'end_user' && (item.text === 'Sites' || item.text === 'Devices')) return true;
        
        return false;
      });
    }
    
    return [];
  };

  const fullMenuItems = getMenuItems();
  const bottomNavItems = fullMenuItems.filter(item => item.text !== 'Settings');

  const drawer = (
    <div>
      <Toolbar sx={{ justifyContent: 'center' }}>
        <Typography variant="h6" component="div" sx={{ display: 'flex', alignItems: 'center', fontWeight: 'bold' }}>
          <WaterDropIcon sx={{ mr: 1, color: theme.palette.primary.main, fontSize: 28 }} />
          Thynk H2O
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {fullMenuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton 
              selected={location.pathname === item.path}
              onClick={() => {
                navigate(item.path);
                if (isMobile) {
                  setMobileOpen(false);
                }
              }}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: theme.palette.action.selected,
                  '&:hover': {
                    backgroundColor: theme.palette.action.selected,
                  },
                },
              }}
            >
              <ListItemIcon sx={{ 
                color: location.pathname === item.path ? theme.palette.primary.main : 'inherit',
                minWidth: '40px'
              }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text} 
                primaryTypographyProps={{
                  fontWeight: location.pathname === item.path ? 'bold' : 'normal'
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </div>
  );

  const bottomNavValue = bottomNavItems.findIndex(item => location.pathname.startsWith(item.path));

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      width: '100%', 
      minHeight: '100vh',
      pb: isMobile ? '56px' : 0 // Add padding for bottom navigation
    }}>
      <AppBar
        position="fixed"
        sx={{
          width: '100%',
          boxShadow: 1,
          backgroundColor: theme.palette.background.paper,
          color: 'text.primary',
          zIndex: theme.zIndex.drawer + 1
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap component="div" sx={{ display: 'flex', alignItems: 'center', fontWeight: 'bold', mr: 3 }}>
              <WaterDropIcon sx={{ mr: 1, color: theme.palette.primary.main, fontSize: 28 }} />
              Thynk H2O
            </Typography>
            
            {/* Desktop navigation */}
            <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
              {fullMenuItems.map((item) => (
                <Button
                  key={item.text}
                  startIcon={item.icon}
                  color={location.pathname === item.path ? 'primary' : 'inherit'}
                  onClick={() => navigate(item.path)}
                  sx={{ 
                    mx: 1,
                    fontWeight: location.pathname === item.path ? 'bold' : 'normal',
                    borderBottom: location.pathname === item.path ? `2px solid ${theme.palette.primary.main}` : 'none'
                  }}
                >
                  {item.text}
                </Button>
              ))}
            </Box>
          </Box>
          
          {user ? (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Tooltip title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
                <IconButton
                  onClick={toggleColorMode}
                  color="inherit"
                  sx={{ mr: 1 }}
                >
                  {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
                </IconButton>
              </Tooltip>

              {/* Refresh Button */}
              <Tooltip title="Refresh all data">
                <IconButton
                  onClick={refreshData}
                  disabled={isRefreshing}
                  color="primary"
                  sx={{ mr: 1 }}
                >
                  {isRefreshing ? (
                    <CircularProgress size={24} color="primary" />
                  ) : (
                    <RefreshIcon />
                  )}
                </IconButton>
              </Tooltip>
              
              <Typography variant="body2" sx={{ mr: 2, display: { xs: 'none', sm: 'block' } }}>
                {user.firstName} {user.lastName}
              </Typography>
              <Tooltip title="Account settings">
                <IconButton
                  onClick={handleProfileMenuOpen}
                  size="large"
                  edge="end"
                  aria-label="account of current user"
                  aria-haspopup="true"
                  color="inherit"
                >
                  <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 32, height: 32 }}>
                    {user.firstName?.charAt(0) || 'U'}
                  </Avatar>
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleProfileMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <MenuItem onClick={handleProfileMenuClose} disabled>
                  <ListItemIcon>
                    <PersonIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={`${user.firstName || ''} ${user.lastName || ''}`}
                    secondary={user.role ? user.role.replace('_', ' ') : ''}
                  />
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => {
                  handleProfileMenuClose();
                  navigate('/profile');
                }}>
                  <ListItemIcon>
                    <AccountIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="My Profile" />
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Logout" />
                </MenuItem>
              </Menu>
            </Box>
          ) : (
            <Button 
              color="primary" 
              variant="contained" 
              startIcon={<AccountIcon />}
              onClick={() => navigate('/login')}
            >
              Login
            </Button>
          )}
        </Toolbar>
      </AppBar>
      {/* Mobile Drawer */}
      <Box component="nav" sx={{ width: { md: 0 }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              zIndex: theme.zIndex.drawer + 2
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: '100%',
          marginTop: '64px',
          marginBottom: { xs: '56px', md: 0 },
          maxWidth: { xs: '100%', md: '90%' },
          mx: 'auto',
          boxSizing: 'border-box'
        }}
      >
        <Outlet />
      </Box>

      {/* Bottom Navigation for Mobile */}
      <Paper 
        sx={{ 
          position: 'fixed', 
          bottom: 0, 
          left: 0, 
          right: 0, 
          display: { xs: 'block', md: 'none' },
          zIndex: theme.zIndex.appBar,
          borderTop: `1px solid ${theme.palette.divider}`
        }} 
        elevation={3}
      >
        <BottomNavigation
          showLabels
          value={bottomNavValue >= 0 ? bottomNavValue : false}
          onChange={(event, newValue) => {
            navigate(bottomNavItems[newValue].path);
          }}
          sx={{
            backgroundColor: 'background.paper',
            height: '56px',
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.7rem',
              '&.Mui-selected': {
                fontSize: '0.7rem',
                fontWeight: 'bold'
              }
            },
            '& .MuiBottomNavigationAction-root.Mui-selected': {
              color: theme.palette.primary.main
            }
          }}
        >
          {bottomNavItems.map((item) => (
            <BottomNavigationAction 
              key={item.text} 
              label={item.text} 
              icon={item.icon} 
              sx={{
                minWidth: 'auto',
                padding: '6px 0',
                '& .MuiBottomNavigationAction-label': {
                  marginTop: '4px'
                }
              }}
            />
          ))}
        </BottomNavigation>
      </Paper>
    </Box>
  );
}

export default Layout;
